import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, X } from 'lucide-react';

interface CheckingStatusCardProps {
  isVisible: boolean;
  isChecking: boolean;
  isComplete: boolean;
  currentCount: number;
  totalCount: number;
  failedCount?: number;
  onClose: () => void;
}

export const CheckingStatusCard: React.FC<CheckingStatusCardProps> = ({
  isVisible,
  isChecking,
  isComplete,
  currentCount,
  totalCount,
  failedCount = 0,
  onClose
}) => {
  if (!isVisible) return null;

  return (
    <Card className="border-l-4 border-l-blue-500 bg-blue-50 dark:bg-blue-950/20">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isComplete ? (
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            ) : (
              <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
            )}
            
            <div>
              {isComplete ? (
                <div>
                  <div className="font-semibold text-green-800 dark:text-green-200">
                    Indexing check complete!
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300">
                    {failedCount > 0 ? (
                      <>
                        Successfully checked {currentCount} terms
                        {failedCount > 0 && (
                          <div className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                            {failedCount} terms failed - click "Sync Sheets Terms" to retry failed terms
                          </div>
                        )}
                      </>
                    ) : (
                      `Successfully checked ${currentCount} terms`
                    )}
                  </div>
                </div>
              ) : isChecking ? (
                <div>
                  <div className="font-semibold text-blue-800 dark:text-blue-200">
                    Checking Amazon indexing...
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    {currentCount} of {totalCount} terms checked
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-blue-800 dark:text-blue-200">
                    Preparing to check terms...
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    {totalCount} terms queued for checking
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {isComplete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {!isComplete && (
          <div className="mt-3">
            <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
              <div 
                className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ 
                  width: totalCount > 0 ? `${(currentCount / totalCount) * 100}%` : '0%' 
                }}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CheckingStatusCard;